import React from "react"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import { Button, Grid, Typography } from "@material-ui/core"
import { HackleExperiment, HackleVariation, useTrack } from "@hackler/react-sdk"
import { FormattedMessage, useIntl } from "react-intl"
import tooltip from "../../../assets/images/tooltip.png"
import classNames from "classnames"
import { QuestionTooltip } from "../../../components/units/QuestionTooltip"
import { GAEvent } from "../../../modules/GoogleAnalytics"
import CommonBenefit from "./CommonBenefit"
import usePlatform from "../../../hooks/usePlatform"
import useLoginPopup from "../../../hooks/useLoginPopup"
import { Locale } from "../../../locale"
import SmallCheckIcon from "../../../assets/images/icon/SmallCheckIcon"
import ArrowIcon from "../../../assets/images/icon/ArrowIcon"
import PricingCalculator from "../components/PricingCalculator"
import { PRICING_CALCULATOR } from "../../../abTestKey"

interface StyleProps {
  lang: string
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "center",
    },
    h6: {
      fontSize: 20,
      [theme.breakpoints.up("md")]: {
        fontSize: 24,
      },
    },
    title: {
      marginBottom: 34,
    },
    priceCard: {
      width: 340,
      height: 630,
      margin: "0 auto",
      padding: "21px 0",
      border: "1px solid #EDEDED",
      borderRadius: "20px",
      background: "#fff",
      boxShadow: "0px 24px 40px rgba(0, 0, 0, 0.05)",
      textAlign: "center",
      overflow: "hidden",
      [theme.breakpoints.up("md")]: {
        height: ({ lang }) => (lang === "ko" ? 767 : 792),
        width: 384,
      },
    },
    planDesc: {
      padding: ({ lang }) => (lang === "ko" ? "10px 12px" : "10px 0"),
      "& ul": {
        height: 215,
        padding: 0,
        margin: 0,
      },
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.up("md")]: {
        height: 358,
      },
    },
    buttonWrap: {
      padding: "0 32px",
      marginBottom: ({ lang }) => (lang === "ko" ? 36 : 20),
      [theme.breakpoints.up("md")]: {
        marginBottom: ({ lang }) => (lang === "ko" ? 36 : 36),
      },
    },
    button: {
      textTransform: "none",
      borderRadius: 12,
      fontSize: "18px",
      padding: "12px 20px",
      height: "54px",
    },
    primaryColor: {
      color: theme.palette.primary.main,
    },
    container: {
      height: 690,
      width: 340,
      borderRadius: 20,
      [theme.breakpoints.up("md")]: {
        height: ({ lang }) => (lang === "ko" ? 830 : 858),
        width: 396,
      },
    },
    promotionContainer: {
      background: "#E5F6EC",
      boxShadow: "0px 24px 40px rgba(0, 0, 0, 0.05)",
      marginTop: 24,
      [theme.breakpoints.up("md")]: {
        marginTop: 0,
      },
    },
    promotionTitle: {
      fontSize: 20,
      fontWeight: 800,
      color: "#0A9541",
      marginBottom: 2,
    },
    eventPrice: {
      width: "100%",
      height: 152,
      padding: "0 8px",
      margin: "24px 0 24px",
      background: "#E5F6EC",
      textAlign: "left",
    },
    grayPrice: {
      width: "100%",
      height: 134,
      padding: "0 8px",
      margin: "26px 0 48px",
      "& p": {
        fontSize: 40,
      },
    },
    enterpriseReq: {
      background: `url(${tooltip}) no-repeat center center`,
      height: 134,
      margin: "54px 0 48px",
      "& p": {
        fontSize: 32,
      },
    },
    finalPrice: {
      fontSize: 32,
      fontWeight: 700,
      color: theme.palette.primary.main,
      opacity: 1,
    },
    li: {
      padding: ({ lang }) => (lang === "ko" ? "0 20px" : "0 12px 0 20px"),
      marginBottom: 10,
      textAlign: "left",
      listStyle: "none",
      color: "rgba(0,0,0,0.87)",
      "& div": {
        flexWrap: "nowrap",
        wordSpacing: ({ lang }) => (lang === "ko" ? 0 : "-1px"),
      },
      "& div > p": {
        fontSize: 16,
      },
      "& svg": {
        fontSize: "22px",
        color: "rgba(0,0,0,0.3)",
        marginTop: "8px",
        marginRight: "15px",
      },
      [theme.breakpoints.up("md")]: {
        padding: ({ lang }) => (lang === "ko" ? "0 30px" : "0 12px 0 20px"),
        marginBottom: 21,
        "& div > p": {
          fontSize: ({ lang }) => (lang === "ko" ? 20 : 19),
        },
      },
    },
    blue: {
      color: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.main,
      },
    },
    caption: {
      fontSize: "12px !important",
      color: "rgba(0,0,0,.6)",
      [theme.breakpoints.up("md")]: {
        fontSize: "14px !important",
      },
    },
    subtitle1: {
      fontSize: 14,
      [theme.breakpoints.up("md")]: {
        fontSize: 16,
      },
      "& svg": {
        margin: "0 16px",
      },
    },
    bottomCaption: {
      fontSize: 12,
      color: "rgba(0,0,0,.3)",
      textAlign: "center",
      [theme.breakpoints.up("md")]: {
        fontSize: 14,
      },
    },
  })
)

interface Props {
  handleOpen(): void
  inquire(): void
}

export default function PricingCardsNew({ handleOpen, inquire }: Props) {
  const intl = useIntl()
  const track = useTrack()
  const { isMobile } = usePlatform()
  const loginPopup = useLoginPopup()
  const lang = Locale.getLangCode()
  const classes = useStyles({ lang })

  return (
    <Grid className={classes.root}>
      <Grid container spacing={3} justify={"center"} alignItems={"flex-end"}>
        <Grid
          item
          className={classes.container}
          style={{ paddingTop: isMobile ? 0 : 45 }}
          container
          alignItems={"flex-end"}>
          <div className={classes.priceCard}>
            <Typography variant={"h6"} className={classes.h6}>
              <strong>
                <FormattedMessage id={"free_plan"} />
              </strong>
            </Typography>
            <Typography variant={"subtitle1"} style={{ opacity: 0.6 }} className={classes.subtitle1}>
              <FormattedMessage id={"free_plan_desc"} />
              <QuestionTooltip message={intl.formatMessage({ id: "event_tooltip" }, { br: <br /> })!} />
            </Typography>
            <Grid container spacing={1}>
              <Grid container justify={"center"} alignItems={"center"} className={classNames([classes.grayPrice])}>
                <Typography>
                  <strong>Free</strong>
                </Typography>
              </Grid>
            </Grid>
            <div className={classes.buttonWrap}>
              <Button
                color={"primary"}
                variant={"contained"}
                onClick={() => {
                  GAEvent({
                    category: "pricing",
                    action: "click",
                    label: "start start-up free plan",
                  })
                  track({ key: "homepage_pricing_compare_main_free_plan_click" })
                  loginPopup()
                }}
                className={classes.button}
                style={{ boxShadow: "none" }}
                fullWidth={true}>
                <strong>
                  <FormattedMessage id={"btn_start_free_plan"} />
                </strong>
              </Button>
            </div>
            <Grid className={classes.planDesc}>
              <ul>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"plan_benefit_many_kinds_of_sdk"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"plan_benefit_test_platform_line1"} />
                      <br />
                      <FormattedMessage id={"plan_benefit_test_platform_line2"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"plan_benefit_change_real_time_line1"} />
                      <br />
                      <FormattedMessage id={"plan_benefit_change_real_time_line2"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"collaborator_limit_free"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"data_archiving_limit_free_plan"} />
                    </Typography>
                  </Grid>
                </li>
              </ul>
            </Grid>
          </div>
        </Grid>
        <Grid
          item
          className={classNames([classes.container, classes.promotionContainer])}
          container
          alignItems={"flex-end"}>
          <Grid container justify={"center"}>
            <Typography className={classes.promotionTitle}>
              {/* 프로모션 기간은 당월의 마지막 주에 다음달 말일로 변경 */}
              {/* 프로모션 기간 갱신을 자동화 하지 않는 이유 - 회사 전체 리마인드 차원 */}
              <FormattedMessage id={"promotion_time_limit"} values={{ date: <span>10/31</span> }} />
            </Typography>
          </Grid>
          <div className={classes.priceCard}>
            <Typography variant={"h6"} className={classes.h6}>
              <strong>
                <FormattedMessage id={"pro_plan"} />
              </strong>
            </Typography>
            <Typography variant={"subtitle1"} style={{ opacity: 0.6 }} className={classes.subtitle1}>
              <FormattedMessage id={"pro_plan_desc"} />
            </Typography>
            <Grid container justify={"center"} alignItems={"center"}>
              <Grid container justify={"center"} alignItems={"center"} className={classes.eventPrice}>
                <Grid
                  item
                  xs={isMobile ? 9 : 8}
                  container
                  style={{ padding: "20px 20px 0 20px" }}
                  alignItems={"center"}>
                  <Grid item xs={12} container alignItems={"flex-end"} justify={"space-between"}>
                    <Grid item>
                      <Typography variant={"subtitle1"} style={{ opacity: 0.6 }} className={classes.subtitle1}>
                        <FormattedMessage id={"payment_event"} />
                        <br />
                        <strong style={{ fontSize: isMobile ? 20 : 24 }}>
                          {lang === "ko" ? (
                            <>
                              <del>0.2</del>원
                            </>
                          ) : (
                            <del>$0.2</del>
                          )}
                        </strong>{" "}
                        <ArrowIcon.Right size={20} fillColor={"black"} fillOpacity={0.6} />
                      </Typography>
                    </Grid>
                    <Typography className={classes.finalPrice}>
                      {lang === "ko" ? <strong>0.1원</strong> : <strong>$0.1</strong>}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography className={classes.bottomCaption}>
                  <FormattedMessage id={"pro_plan_usage_limit"} />
                </Typography>
              </Grid>
            </Grid>
            <div className={classes.buttonWrap}>
              <Button
                color={"primary"}
                variant={"contained"}
                onClick={() => {
                  GAEvent({
                    category: "pricing",
                    action: "click",
                    label: "start pro plan",
                  })
                  track({ key: "homepage_pricing_compare_main_pro_plan_free_trial_click" })
                  loginPopup()
                }}
                className={classes.button}
                style={{ boxShadow: "none" }}
                fullWidth={true}>
                <strong>
                  <FormattedMessage id={"BTN_START_ABTEST"} />
                </strong>
              </Button>
            </div>
            <Grid className={classes.planDesc}>
              <ul>
                <li className={classes.li}>
                  <Grid container spacing={1} className={classes.blue}>
                    <SmallCheckIcon color={"#2962FF"} opacity={1} />
                    <Typography>
                      <FormattedMessage id={"pro_plan_benefit_free_trial_period"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1} className={classes.blue}>
                    <SmallCheckIcon color={"#2962FF"} opacity={1} />
                    <Typography>
                      <FormattedMessage id={"pro_plan_include_free_plan"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1} className={classes.blue}>
                    <SmallCheckIcon color={"#2962FF"} opacity={1} />
                    <div>
                      <Typography>
                        <FormattedMessage id={"pro_plan_benefit_segment"} />
                      </Typography>
                      <Typography className={classes.caption}>
                        <FormattedMessage id={"pro_plan_benefit_detail_line1"} />
                        <br />
                        <FormattedMessage id={"pro_plan_benefit_detail_line2"} />
                      </Typography>
                    </div>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"data_archiving_limit_pro_plan"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"email_support"} />
                    </Typography>
                  </Grid>
                </li>
              </ul>
            </Grid>
          </div>
        </Grid>
        <Grid
          item
          className={classes.container}
          style={{ paddingTop: isMobile ? 24 : 45 }}
          container
          alignItems={"flex-end"}>
          <div className={classes.priceCard}>
            <Typography variant={"h6"} className={classes.h6}>
              <strong>
                <FormattedMessage id={"enterprise_plan"} />
              </strong>
            </Typography>
            <Grid container spacing={1}>
              <Grid container justify={"center"} alignItems={"center"} className={classNames([classes.enterpriseReq])}>
                <Typography>
                  <strong>
                    <FormattedMessage id={"contact_us_separately"} />
                  </strong>
                </Typography>
              </Grid>
            </Grid>
            <div className={classes.buttonWrap}>
              <Button
                color={"primary"}
                variant={"outlined"}
                onClick={handleOpen}
                className={classes.button}
                style={{ boxShadow: "none" }}
                fullWidth={true}>
                <strong>
                  <FormattedMessage id={"btn_inquire"} />
                </strong>
              </Button>
            </div>
            <Grid className={classes.planDesc}>
              <ul>
                <li className={classes.li}>
                  <Grid container spacing={1} className={classes.blue}>
                    <SmallCheckIcon color={"#2962FF"} opacity={1} />
                    <Typography>
                      <FormattedMessage id={"enterprise_plan_include_pro_plan"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1} className={classes.blue}>
                    <SmallCheckIcon color={"#2962FF"} opacity={1} />
                    <Typography>
                      <FormattedMessage id={"enterprise_plan_benefit1"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"enterprise_plan_benefit2"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"enterprise_plan_benefit4"} />
                    </Typography>
                  </Grid>
                </li>
                <li className={classes.li}>
                  <Grid container spacing={1}>
                    <SmallCheckIcon />
                    <Typography>
                      <FormattedMessage id={"enterprise_plan_benefit5"} />
                    </Typography>
                  </Grid>
                </li>
              </ul>
            </Grid>
          </div>
        </Grid>
        <HackleExperiment experimentKey={PRICING_CALCULATOR}>
          <HackleVariation variation={"A"}>
            <CommonBenefit />
          </HackleVariation>
          <HackleVariation variation={"B"}>
            <PricingCalculator inquire={inquire} />
          </HackleVariation>
        </HackleExperiment>
      </Grid>
    </Grid>
  )
}
